import { Button, Steps, notification } from "antd";
import dayjs from "dayjs";
import { Formik } from "formik";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { BiChevronLeft, BiChevronRight, BiSave } from "react-icons/bi";
import Header from "shared/components/Header";
import ConfigureBenefits from "./Steps/ConfigureBenefits";
import PlanDetails from "./Steps/PlanDetails";
import ReviewSummary from "./Steps/ReviewSummary";
import SelectCoverages from "./Steps/SelectCoverages";
import { stepValidations } from "./validation";

const STEPS = [
  { title: "Détails du plan" },
  { title: "Sélection des garanties" },
  { title: "Prestations & Tarification" },
  { title: "Récapitulatif" },
];

const buildInitialValues = (data) => ({
  name: get(data, "name", ""),
  identifier: get(data, "identifier", ""),
  description: get(data, "description", ""),
  risk: get(data, "risk", "health"),
  status: get(data, "status", "ACTIVE"),
  category: get(data, "category", ""),
  level: get(data, "level", null),
  startDate: get(data, "startDate") || dayjs().toISOString(),
  endDate: get(data, "endDate") || dayjs().add(1, "year").toISOString(),
  params: (get(data, "params") || []).map((param) => ({
    id: param.id,
    rules: (get(param, "rules") || []).reduce(
      (acc, rule) => ({
        ...acc,
        [rule.name]: { id: rule.id, name: rule.name, value: rule.value },
      }),
      {},
    ),
  })),
  pricingMode: get(data, "pricingMode"),
  zoneType: get(data, "zoneType"),
  zones: get(data, "zones") || [],
  ageBrackets: get(data, "ageBrackets") || [],
  regimes: get(data, "regimes") || [],
  insurancePlanCoverages: (get(data, "insurancePlanCoverages") || []).map(
    (coverage) => ({
      id: coverage.id,
      pricingMode: get(coverage, "pricingMode"),
      coverageCategory: coverage.coverageCategory,
      needs: get(coverage, "needs") || { level: 1 },
      pricer: coverage.pricer,
      coverageMappings: (get(coverage, "coverageMappings") || []).map(
        (mapping) => ({
          id: mapping.id,
          waitingDays: get(mapping, "waitingDays", 0),
          brssPercentage: get(mapping, "brssPercentage"),
          pmssPercentage: get(mapping, "pmssPercentage"),
          reimbursementAmount: get(mapping, "reimbursementAmount"),
          carenceDays: get(mapping, "carenceDays"),
          franchiseAmount: get(mapping, "franchiseAmount"),
          coverage: mapping.coverage,
          limits: get(mapping, "limits") || [],
          supportingInformation: get(mapping, "supportingInformation") || [],
          pricer: mapping.pricer,
        }),
      ),
    }),
  ),
});

const buildPlanPayload = (values) => ({
  name: values.name,
  identifier: values.identifier,
  description: values.description,
  risk: values.risk,
  status: values.status,
  category: values.category,
  level: values.level,
  startDate: values.startDate,
  endDate: values.endDate,
  pricingMode: values.pricingMode,
});

const buildCoveragesPayload = (values) => ({
  insurancePlanCoverages: (values.insurancePlanCoverages || []).map((cov) => ({
    id: cov.id,
    pricingMode: cov.pricingMode,
    coverageCategory: { id: get(cov, "coverageCategory.id") },
    needs: cov.needs ? { id: cov.needs.id, level: cov.needs.level } : null,
    coverageMappings: (get(cov, "coverageMappings") || []).map((mapping) => ({
      id: mapping.id,
      waitingDays: mapping.waitingDays,
      coverage: { id: get(mapping, "coverage.id") },
      limits: (get(mapping, "limits") || []).map((limit) => ({
        id: limit.id,
        code: limit.code,
        value: limit.value,
      })),
    })),
  })),
});

const Configure = ({ data, updateInsurancePlan, cancel, refetch }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const saveProgress = async (values) => {
    const where = { id: data.id };
    await updateInsurancePlan({
      variables: { where, data: buildPlanPayload(values) },
    });
    if ((values.insurancePlanCoverages || []).length > 0) {
      await updateInsurancePlan({
        variables: { where, data: buildCoveragesPayload(values) },
      });
    }
  };

  const handleNext = async (values) => {
    const validation = stepValidations[currentStep];
    if (validation) {
      try {
        await validation.validate(values, { abortEarly: false });
      } catch (err) {
        const errors = get(err, "errors", []);
        notification.warning({
          message: "Validation",
          description: errors[0] || "Veuillez remplir les champs obligatoires",
        });
        return;
      }
    }

    setSaving(true);
    try {
      await saveProgress(values);
      if (refetch) await refetch();
      notification.success({ message: "Progression enregistrée" });
      setCurrentStep((prev) => prev + 1);
    } catch (err) {
      notification.error({ message: "Échec de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => setCurrentStep((prev) => prev - 1);

  const handleFinalSave = async (values) => {
    setSaving(true);
    try {
      await saveProgress(values);
      notification.success({ message: "Configuration enregistrée" });
    } catch (err) {
      notification.error({ message: "Échec de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  const renderStep = (formikProps) => {
    const { values, setFieldValue, errors, touched, handleChange } =
      formikProps;
    const stepProps = {
      values,
      setFieldValue,
      errors,
      touched,
      refetch,
      handleChange,
      data,
    };
    switch (currentStep) {
      case 0:
        return <PlanDetails {...stepProps} />;
      case 1:
        return <SelectCoverages {...stepProps} />;
      case 2:
        return <ConfigureBenefits {...stepProps} />;
      case 3:
        return <ReviewSummary {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Header
        key="header"
        title={`Configurer : ${data.name}`}
        actions={[
          {
            text: "Annuler",
            onClick: cancel,
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
        ]}
      />
      <div className="configure-wizard">
        <Steps
          current={currentStep}
          items={STEPS}
          className="m-bottom-24"
          onChange={(step) => {
            if (step < currentStep) setCurrentStep(step);
          }}
        />
        <Formik
          initialValues={buildInitialValues(data)}
          onSubmit={handleFinalSave}
          enableReinitialize
        >
          {(formikProps) => (
            <div>
              <div className="configure-wizard__content">
                {renderStep(formikProps)}
              </div>
              <div className="configure-wizard__actions d-flex justify--between m-top-24">
                <div>
                  {currentStep > 0 && (
                    <Button
                      type="default"
                      size="large"
                      onClick={handlePrevious}
                      icon={<BiChevronLeft size={18} />}
                    >
                      Précédent
                    </Button>
                  )}
                </div>
                <div>
                  {currentStep < STEPS.length - 1 ? (
                    <Button
                      type="primary"
                      size="large"
                      loading={saving}
                      onClick={() => handleNext(formikProps.values)}
                    >
                      Enregistrer & Suivant
                      <BiChevronRight size={18} />
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      size="large"
                      loading={saving}
                      onClick={() => handleFinalSave(formikProps.values)}
                      icon={<BiSave size={18} />}
                    >
                      Enregistrer la configuration
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </>
  );
};

export default Configure;
